/**
 * Math Utilities for Invoice Calculations
 * Ensures all calculations use maximum 2 decimal places
 */

/**
 * Round number to maximum 2 decimal places
 * @param {number|string} value - Value to round
 * @returns {number} - Rounded value with max 2 decimal places
 */
export const roundToTwoDecimals = (value) => {
  if (value === null || value === undefined || value === '') {
    return 0;
  }
  
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return 0;
  }
  
  // Round to 2 decimal places using Math.round to avoid floating point issues
  return Math.round(numValue * 100) / 100;
};

/**
 * Calculate sales tax amount with 2 decimal precision
 * @param {number} baseAmount - Base amount before tax
 * @param {number} taxRate - Tax rate as percentage (e.g., 18 for 18%)
 * @returns {number} - Tax amount rounded to 2 decimal places
 */
export const calculateSalesTax = (baseAmount, taxRate) => {
  const base = roundToTwoDecimals(baseAmount);
  const rate = roundToTwoDecimals(taxRate);

  const taxAmount = (base * rate) / 100;
  // Return with 2 decimal places to show decimal tax values
  return roundToTwoDecimals(taxAmount);
};

/**
 * Calculate sales tax for 3rd Schedule Goods (SN008, SN027)
 * Tax is calculated on Fixed Notified Value/Retail Price, not transaction value
 * @param {object} item - Invoice item object
 * @param {number} taxRate - Tax rate as percentage (e.g., 18 for 18%)
 * @param {string} scenarioId - Optional scenario ID to apply specific rules
 * @returns {number} - Tax amount rounded to 2 decimal places
 */
export const calculateThirdScheduleSalesTax = (item, taxRate, scenarioId) => {
  const fixedNotifiedValue = roundToTwoDecimals(item.fixedNotifiedValueOrRetailPrice || 0);
  const quantity = roundToTwoDecimals(item.quantity || 1);
  const rate = roundToTwoDecimals(taxRate);
  
  // For SN008 and other 3rd Schedule Goods: tax on fixed value × quantity × rate
  if (scenarioId === 'SN008') {
    const taxBase = roundToTwoDecimals(fixedNotifiedValue * quantity);
    const taxAmount = (taxBase * rate) / 100;
    // Return with 2 decimal places (do not round to integer)
    return roundToTwoDecimals(taxAmount);
  } else {
    const taxBase = roundToTwoDecimals(fixedNotifiedValue * quantity);
    const taxAmount = (taxBase * rate) / 100;
    // Return with 2 decimal places (do not round to integer)
    return roundToTwoDecimals(taxAmount);
  }
};

/**
 * Calculate sales tax based on scenario type
 * @param {object} item - Invoice item object
 * @param {number} taxRate - Tax rate as percentage (e.g., 18 for 18%)
 * @param {string} scenarioId - Scenario ID (e.g., 'SN008', 'SN027')
 * @returns {number} - Tax amount rounded to 2 decimal places
 */
export const calculateSalesTaxByScenario = (item, taxRate, scenarioId) => {
  // 3rd Schedule Goods scenarios use Fixed Notified Value for tax calculation
  if (scenarioId === 'SN008' || scenarioId === 'SN027' || item.saleType === '3rd Schedule Goods') {
    return calculateThirdScheduleSalesTax(item, taxRate, scenarioId);
  }

  // Special calculation logic for fixed amount tax scenarios (SN021, SN022, SN023)
  // These scenarios use quantity-based calculations instead of percentage-based
  // Note: For scenarios page, this will use fallback values and trigger async database update
  if (scenarioId === 'SN021' || scenarioId === 'SN022' || scenarioId === 'SN023') {
    return calculateSpecialScenarioTaxSync(item, taxRate, scenarioId);
  }

  // Standard scenarios use transaction value for tax calculation
  const quantity = roundToTwoDecimals(item.quantity || 1);
  const valueSalesExcludingST = roundToTwoDecimals(item.valueSalesExcludingST || 0);
  const baseAmount = roundToTwoDecimals(quantity * valueSalesExcludingST);

  return calculateSalesTax(baseAmount, taxRate);
};

/**
 * Calculate sales tax for special scenarios (SN021, SN022, SN023) - Synchronous version
 * Uses fallback calculations that will be updated by async database calls in the UI
 * @param {object} item - Invoice item object
 * @param {number} taxRate - Tax rate (not used for these scenarios, but kept for compatibility)
 * @param {string} scenarioId - Scenario ID (SN021, SN022, or SN023)
 * @returns {number} - Tax amount rounded to 2 decimal places
 */
export const calculateSpecialScenarioTaxSync = (item, taxRate, scenarioId) => {
  const quantity = roundToTwoDecimals(item.quantity || 1);
  const valueSalesExcludingST = roundToTwoDecimals(item.valueSalesExcludingST || 0);

  console.log(`🧮 Calculating special scenario tax (sync) for ${scenarioId}`, { quantity, valueSalesExcludingST });

  if (scenarioId === 'SN021') {
    // SN021: Fixed amount Rs.3 per unit (will be updated from database)
    return roundToTwoDecimals(quantity * 3);
  } else if (scenarioId === 'SN022') {
    // SN022: 18% + Rs.60 per unit (will be updated from database)
    const percentageTax = (valueSalesExcludingST * 18) / 100;
    const fixedAmountTax = quantity * 60;
    return roundToTwoDecimals(percentageTax + fixedAmountTax);
  } else if (scenarioId === 'SN023') {
    // SN023: Fixed amount Rs.200 per unit (will be updated from database)
    return roundToTwoDecimals(quantity * 200);
  }

  return 0;
};

/**
 * Calculate sales tax for special scenarios (SN021, SN022, SN023) using dynamic database formulas
 * Async version for direct API calls
 * @param {object} item - Invoice item object
 * @param {number} taxRate - Tax rate (not used for these scenarios, but kept for compatibility)
 * @param {string} scenarioId - Scenario ID (SN021, SN022, or SN023)
 * @returns {Promise<number>} - Tax amount rounded to 2 decimal places
 */
export const calculateSpecialScenarioTax = async (item, taxRate, scenarioId) => {
  try {
    console.log(`🧮 Calculating special scenario tax (async) for ${scenarioId}`, { item, taxRate });

    const quantity = roundToTwoDecimals(item.quantity || 1);
    const valueSalesExcludingST = roundToTwoDecimals(item.valueSalesExcludingST || 0);

    // For scenarios page, we need to make an API call to get the database calculation
    // Import api dynamically to avoid circular dependencies
    const api = (await import('../services/api')).default;

    const response = await api.post('/scenarios/calculate-formula', {
      scenarioId: scenarioId,
      baseAmount: valueSalesExcludingST,
      quantity: quantity
    });

    if (response.data && response.data.success) {
      const calculatedTax = roundToTwoDecimals(response.data.result.salesTaxApplicable);
      console.log(`✅ Database calculation result for ${scenarioId}: ${calculatedTax}`);
      return calculatedTax;
    } else {
      console.warn(`⚠️ Database calculation failed for ${scenarioId}, using fallback`);
      // Fallback to sync calculation
      return calculateSpecialScenarioTaxSync(item, taxRate, scenarioId);
    }

  } catch (error) {
    console.error(`❌ Error calculating special scenario tax for ${scenarioId}:`, error);
    // Fallback to sync calculation
    return calculateSpecialScenarioTaxSync(item, taxRate, scenarioId);
  }
};



/**
 * Calculate sales tax using database formula
 * @param {object} item - Invoice item object
 * @param {string} formula - Formula from scenarios.formula field
 * @returns {number} - Tax amount rounded to 2 decimal places
 */
export const calculateSalesTaxByFormula = (item, formula) => {
  if (!formula) return 0;
  
  const quantity = roundToTwoDecimals(item.quantity || 1);
  const valueSalesExcludingST = roundToTwoDecimals(item.valueSalesExcludingST || 0);
  const fixedNotifiedValueOrRetailPrice = roundToTwoDecimals(item.fixedNotifiedValueOrRetailPrice || 0);
  const singleUnitPrice = roundToTwoDecimals(item.singleUnitPrice || 0);
  
  try {
    // Replace variables in formula with actual values
    let calculationFormula = formula
      .replace(/valueSalesExcludingST/g, valueSalesExcludingST)
      .replace(/fixedNotifiedValueOrRetailPrice/g, fixedNotifiedValueOrRetailPrice)
      .replace(/singleUnitPrice/g, singleUnitPrice)
      .replace(/quantity/g, quantity);
    
    // Handle mathematical operations safely
    // Support basic operations: +, -, *, /, parentheses
    const result = evaluateFormula(calculationFormula);
    return roundToTwoDecimals(result);
    
  } catch (error) {
    console.error('❌ Formula calculation error:', error, 'Formula:', formula);
    return 0;
  }
};

/**
 * Safely evaluate mathematical formula
 * @param {string} formula - Mathematical formula to evaluate
 * @returns {number} - Calculated result
 */
export const evaluateFormula = (formula) => {
  try {
    const compactFormula = String(formula).replace(/\s+/g, '');
    if (!compactFormula || /[^0-9+\-*/.()]/.test(compactFormula)) {
      throw new Error('Formula contains unsupported characters');
    }

    const tokens = compactFormula.match(/\d*\.?\d+|[()+\-*/]/g) || [];
    if (tokens.join('') !== compactFormula) throw new Error('Formula is malformed');
    let position = 0;

    const parseFactor = () => {
      const token = tokens[position];
      if (token === '+' || token === '-') {
        position += 1;
        const value = parseFactor();
        return token === '-' ? -value : value;
      }
      if (token === '(') {
        position += 1;
        const value = parseExpression();
        if (tokens[position] !== ')') throw new Error('Unbalanced parentheses');
        position += 1;
        return value;
      }
      if (!token || !/^\d*\.?\d+$/.test(token)) throw new Error('Expected a number');
      position += 1;
      return Number(token);
    };

    const parseTerm = () => {
      let value = parseFactor();
      while (tokens[position] === '*' || tokens[position] === '/') {
        const operator = tokens[position++];
        const operand = parseFactor();
        value = operator === '*' ? value * operand : value / operand;
      }
      return value;
    };

    function parseExpression() {
      let value = parseTerm();
      while (tokens[position] === '+' || tokens[position] === '-') {
        const operator = tokens[position++];
        const operand = parseTerm();
        value = operator === '+' ? value + operand : value - operand;
      }
      return value;
    }

    const result = parseExpression();
    if (position !== tokens.length || !Number.isFinite(result)) throw new Error('Formula result is invalid');
    return result;
  } catch (error) {
    console.error('❌ Formula evaluation error:', error);
    return 0;
  }
};

/**
 * Calculate total value including tax with 2 decimal precision
 * @param {number} baseAmount - Base amount before tax
 * @param {number} taxAmount - Tax amount
 * @returns {number} - Total amount rounded to 2 decimal places
 */
export const calculateTotalWithTax = (baseAmount, taxAmount) => {
  const base = roundToTwoDecimals(baseAmount);
  const tax = roundToTwoDecimals(taxAmount);
  
  return roundToTwoDecimals(base + tax);
};

/**
 * Calculate item total with quantity and unit price
 * @param {number} quantity - Item quantity
 * @param {number} unitPrice - Unit price excluding tax
 * @param {number} taxRate - Tax rate as percentage
 * @returns {object} - Calculation breakdown with 2 decimal precision
 */
export const calculateItemTotal = (quantity, unitPrice, taxRate) => {
  const qty = roundToTwoDecimals(quantity);
  const price = roundToTwoDecimals(unitPrice);
  const rate = roundToTwoDecimals(taxRate);
  
  const baseAmount = roundToTwoDecimals(qty * price);
  const taxAmount = calculateSalesTax(baseAmount, rate);
  const totalAmount = calculateTotalWithTax(baseAmount, taxAmount);
  
  return {
    quantity: qty,
    unitPrice: price,
    baseAmount,
    taxRate: rate,
    taxAmount,
    totalAmount
  };
};

/**
 * Calculate WHT (Withholding Tax) with 2 decimal precision
 * @param {number} baseAmount - Base amount for WHT calculation
 * @param {number} whtRate - WHT rate as percentage (e.g., 5 for 5%)
 * @returns {number} - WHT amount rounded to 2 decimal places
 */
export const calculateWHT = (baseAmount, whtRate) => {
  const base = roundToTwoDecimals(baseAmount);
  const rate = roundToTwoDecimals(whtRate);
  
  const whtAmount = (base * rate) / 100;
  return roundToTwoDecimals(whtAmount);
};

/**
 * Calculate FED (Federal Excise Duty) with 2 decimal precision
 * @param {number} baseAmount - Base amount for FED calculation
 * @param {number} fedRate - FED rate as percentage
 * @returns {number} - FED amount rounded to 2 decimal places
 */
export const calculateFED = (baseAmount, fedRate) => {
  const base = roundToTwoDecimals(baseAmount);
  const rate = roundToTwoDecimals(fedRate);
  
  const fedAmount = (base * rate) / 100;
  return roundToTwoDecimals(fedAmount);
};

/**
 * Sum array of values with 2 decimal precision
 * @param {Array<number>} values - Array of numeric values
 * @returns {number} - Sum rounded to 2 decimal places
 */
export const sumWithPrecision = (values) => {
  if (!Array.isArray(values)) return 0;
  
  const sum = values.reduce((total, value) => {
    return total + roundToTwoDecimals(value);
  }, 0);
  
  return roundToTwoDecimals(sum);
};

/**
 * Calculate invoice totals with 2 decimal precision
 * @param {Array} items - Array of invoice items
 * @returns {object} - Invoice totals with 2 decimal precision
 */
export const calculateInvoiceTotals = (items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return {
      totalExclTax: 0,
      totalTax: 0,
      totalInclTax: 0,
      totalWHT: 0,
      totalFED: 0,
      netPayable: 0
    };
  }
  
  let totalExclTax = 0;
  let totalTax = 0;
  let totalWHT = 0;
  let totalFED = 0;
  
  items.forEach(item => {
    const baseAmount = roundToTwoDecimals(item.valueSalesExcludingST || 0);
    const quantity = roundToTwoDecimals(item.quantity || 1);
    const itemBase = roundToTwoDecimals(baseAmount * quantity);
    
    totalExclTax = roundToTwoDecimals(totalExclTax + itemBase);
    
    // Calculate tax
    const taxRate = roundToTwoDecimals(parseFloat((item.rate || '0%').replace('%', '')));
    const taxAmount = calculateSalesTax(itemBase, taxRate);
    totalTax = roundToTwoDecimals(totalTax + taxAmount);
    
    // Add other taxes
    const furtherTax = roundToTwoDecimals(item.furtherTax || 0);
    const fedPayable = roundToTwoDecimals(item.fedPayable || 0);
    const salesTaxWithheld = roundToTwoDecimals(item.salesTaxWithheldAtSource || 0);
    
    totalTax = roundToTwoDecimals(totalTax + furtherTax + fedPayable);
    totalWHT = roundToTwoDecimals(totalWHT + salesTaxWithheld);
    totalFED = roundToTwoDecimals(totalFED + fedPayable);
  });
  
  const totalInclTax = roundToTwoDecimals(totalExclTax + totalTax);
  const netPayable = roundToTwoDecimals(totalInclTax - totalWHT);
  
  return {
    totalExclTax,
    totalTax,
    totalInclTax,
    totalWHT,
    totalFED,
    netPayable
  };
};

/**
 * Format number for display with 2 decimal places
 * @param {number} value - Value to format
 * @param {boolean} showZeroDecimals - Whether to show .00 for whole numbers
 * @returns {string} - Formatted number string
 */
export const formatNumber = (value, showZeroDecimals = true) => {
  const rounded = roundToTwoDecimals(value);
  
  if (showZeroDecimals) {
    return rounded.toFixed(2);
  }
  
  // Remove trailing zeros
  return parseFloat(rounded.toFixed(2)).toString();
};

/**
 * Parse and validate numeric input with 2 decimal precision
 * @param {string|number} input - Input value
 * @param {number} defaultValue - Default value if parsing fails
 * @returns {number} - Parsed and rounded value
 */
export const parseNumericInput = (input, defaultValue = 0) => {
  if (input === null || input === undefined || input === '') {
    return roundToTwoDecimals(defaultValue);
  }
  
  const parsed = parseFloat(input);
  if (isNaN(parsed)) {
    return roundToTwoDecimals(defaultValue);
  }
  
  return roundToTwoDecimals(parsed);
};

/**
 * Validate that a number has at most 2 decimal places
 * @param {number} value - Value to validate
 * @returns {boolean} - True if valid (max 2 decimal places)
 */
export const isValidDecimalPrecision = (value) => {
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return false;
  
  const rounded = roundToTwoDecimals(numValue);
  return Math.abs(numValue - rounded) < 0.001; // Allow for floating point precision
};

/**
 * Convert percentage string to decimal with 2 decimal precision
 * @param {string} percentageStr - Percentage string (e.g., "18%")
 * @returns {number} - Decimal value (e.g., 0.18)
 */
export const percentageToDecimal = (percentageStr) => {
  if (!percentageStr) return 0;
  
  const numStr = percentageStr.toString().replace('%', '');
  const percentage = parseFloat(numStr);
  
  if (isNaN(percentage)) return 0;
  
  return roundToTwoDecimals(percentage / 100);
};

const mathUtils = {
  roundToTwoDecimals,
  calculateSalesTax,
  calculateThirdScheduleSalesTax,
  calculateSalesTaxByScenario,
  calculateSpecialScenarioTax,
  calculateSpecialScenarioTaxSync,
  calculateSalesTaxByFormula,
  evaluateFormula,
  calculateTotalWithTax,
  calculateItemTotal,
  calculateWHT,
  calculateFED,
  sumWithPrecision,
  calculateInvoiceTotals,
  formatNumber,
  parseNumericInput,
  isValidDecimalPrecision,
  percentageToDecimal
};

export default mathUtils;
